import React, { useId, useState } from "react";
import Select, { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";

import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import { styled, SxProps } from "@mui/material/styles";
import { EmtIcon } from "./EmtIcon";
import { EmtTypography } from "./EmtTypography";
import { EmtTheme } from "../types";

export type SelectOptionType = {
  /**
   * Value of option, required
   */
  value: string | number;
  /**
   * Label Text, required
   */
  label: string;
  /**
   * Caption text
   * @default undefined
   */
  caption?: string;
  /**
   * You can pass an <img /> or an <IconComponent/> inside
   * @default undefined
   */
  media?: HTMLImageElement | React.ReactNode;
  /**
   * If entry is "section" entry
   * @default false
   */
  subHeader?: boolean;
  disabled?: boolean; // Add this line
};

export type SelectOptionsType = Array<SelectOptionType>;

export type EmtSelectProps<Value> = Omit<SelectProps<Value>, "onChange"> & {
  /**
   * Label for the input
   */
  inputLabel?: string;
  /**
   * Helper text, pass the error message inside
   * @default undefined
   */
  helperText?: string;
  /**
   * The Input label ID
   * @default undefined
   */
  inputLabelId?: string;
  /**
   * If set, display icon to the left of select, use emt-{icon-name}
   * @default undefined
   */
  leftIconClassName?: string;
  /**
   * Array of SelectOptionsType
   * @default []
   */
  options?: SelectOptionsType;
  /**
   * Style of the container box arround Select
   * @default undefined
   */
  boxSx?: SxProps;
  iconClass?: string;
  nonce?: string;
  color?: string;
  leftIcon?: React.JSX.Element | null;
  onChange?: (value: Value, child: React.ReactNode) => void;
  /**
   * If true, shows a loading indicator
   * @default false
   */
  loading?: boolean;
  children?: React.ReactNode;
  /**
   * If true, prevents the select from opening
   * @default false
   */
  disableOpen?: boolean;
};

const selectBoxBaseStyle = (theme?: EmtTheme) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme?.palette.greyGradient[100]}`,
  borderRadius: "18px 0 0 18px",
  borderRight: 0,
  padding: "10px",
  "& .emt-icons": {
    backgroundColor: theme?.palette.primary.main,
    color: theme?.palette.common.white,
    borderRadius: 8,
    padding: 5.5,
  },
});

const IconBox = styled(Box)(({ theme }: { theme?: EmtTheme }) => ({
  ...selectBoxBaseStyle(theme),
  border: `1px solid ${theme?.palette.greyGradient[100]}`,
  "&.error": {
    borderColor: theme?.palette.error[450],
  },
  "&.success": {
    borderColor: theme?.palette.success.main,
  },
  "&.disabled": {
    "& .emt-icons": {
      backgroundColor: theme?.palette.greyGradient[200],
    },
  },
}));

export const MenuItemBox = styled(Box)(
  ({
    theme,
    color,
  }: {
    theme?: EmtTheme;
    color?:
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning"
      | "purple";
  }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    width: "100%",
    borderRadius: "16px",
    padding: "3px 10px",
    minHeight: "50px",
    "& img": {
      maxWidth: "34px",
      borderRadius: "12px",
    },
    "& span": {
      // color: theme?.palette.greyGradient[250],
    },
    "&.active": {
      background: color
        ? theme?.palette[color]?.main
        : theme?.palette.primary.main,
      color: theme?.palette.common.white,
      "& span": {
        color: theme?.palette.common.white,
      },
    },
    "@media (hover: hover)": {
      "&:hover": {
        background: color
          ? theme?.palette[color]?.main
          : theme?.palette.primary.main,
        color: theme?.palette.common.white,
        "& span": {
          color: theme?.palette.common.white,
        },
      },
    },
  })
);

export const StyledMenuItem = styled(MenuItem)(() => ({
  padding: 5,
  "&:hover": {
    background: "transparent",
  },
  "&.Mui-selected": {
    background: "transparent !important",
    "&:hover": {
      background: "transparent",
    },
  },
  "&.Mui-disabled": {
    opacity: 0.5,
  },
}));

const StyledFormHelperText = styled(FormHelperText)(
  ({ theme }: { theme?: EmtTheme }) => ({
    fontFamily: "Nunito Sans",
    display: "block",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    minWidth: 0,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    flex: 1,
    "&.error": {
      color: theme?.palette.error[450],
    },
  })
);

const StyledListSubheader = styled(ListSubheader)(
  ({ theme }: { theme?: EmtTheme }) => ({
    background: theme?.palette.greyGradient[50],
    borderBottom: `1px solid ${theme?.palette.greyGradient[100]}`,
    padding: "10px 15px",
    marginBottom: "5px",
  })
);

const getSelectedOptions = (options: SelectOptionsType, value: unknown) =>
  Array.isArray(value)
    ? options.filter((o) => value.filter((v) => v === o.value)[0] === o.value)
    : [];

const isOptionSelected = (
  value: unknown,
  option: SelectOptionType,
  multiple: boolean | undefined
): boolean => {
  if (multiple && Array.isArray(value)) {
    return value.filter((v) => option.value === v).length ? true : false;
  }

  return value === option.value;
};

export function EmtSelect<Value>(props: EmtSelectProps<Value>) {
  const {
    variant = "filled",
    helperText,
    inputLabelId,
    inputLabel,
    leftIconClassName,
    leftIcon,
    iconClass,
    onChange,
    onOpen,
    onClose,
    value,
    defaultValue,
    renderValue,
    options = [],
    fullWidth = true,
    boxSx,
    placeholder,
    color,
    loading = false,
    children,
    disableOpen = false,
    ...rest
  } = props;
  const [currentValue, setCurrentValue] = useState(
    value ? value : defaultValue
  );
  const [isOpen, setIsOpen] = useState(false);

  const generatedId = useId();
  const resolvedLabelId = inputLabelId || `${generatedId}-label`;
  const resolvedSelectId = rest.id || `${generatedId}-select`;

  React.useEffect(() => {
    setCurrentValue(value || defaultValue);
  }, [value, defaultValue]);

  const handleChange = (
    event: SelectChangeEvent<any>,
    child: React.ReactNode
  ) => {
    setCurrentValue(event.target.value);
    onChange ? onChange(event.target.value, <>{child}</>) : null;
  };

  const handleOpen = (event: React.SyntheticEvent) => {
    if (disableOpen) return;
    setIsOpen(true);
    onOpen?.(event);
  };

  const handleClose = (event: React.SyntheticEvent) => {
    setIsOpen(false);
    onClose?.(event);
  };

  const handleRenderValue = (value: unknown) => {
    if (!value && placeholder) {
      return <span style={{ color: "#aaa" }}>{placeholder}</span>;
    }

    if (props.multiple && Array.isArray(value)) {
      const selectedOptions = getSelectedOptions(options, value);
      // If options is empty, just show the values as comma-separated
      if (
        selectedOptions.length === 0 &&
        value.length > 0 &&
        options.length === 0
      ) {
        return value.join(", ");
      }
      return selectedOptions.map(
        (option, key) =>
          `${option.label}${key != selectedOptions.length - 1 ? "," : ""} `
      );
    }

    // If no renderValue and currentValue, try to find label in options, else fallback to value as string
    if (!renderValue && currentValue) {
      const found = options.filter((o) => o.value === value)[0];
      if (found) {
        return found.label;
      }
      // fallback: show value as string if not found in options
      return String(value);
    }

    if (typeof renderValue === "function") {
      return renderValue(value as any);
    }

    return renderValue;
  };

  return (
    <Box
      sx={{
        minWidth: 0,
        ...boxSx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
        }}
      >
        {leftIconClassName && (
          <>
            {rest.error ? (
              <IconBox className="error">
                <EmtIcon className={leftIconClassName} />
              </IconBox>
            ) : // @ts-ignore
            rest.color === "success" ? (
              <IconBox className="success">
                <EmtIcon className={leftIconClassName} />
              </IconBox>
            ) : props.disabled ? (
              <IconBox className="disabled">
                <EmtIcon className={leftIconClassName} />
              </IconBox>
            ) : (
              <IconBox>
                <EmtIcon className={leftIconClassName} />
              </IconBox>
            )}
          </>
        )}
        {leftIcon && (
          <>
            <IconBox>
              <EmtIcon>{leftIcon}</EmtIcon>
            </IconBox>
          </>
        )}
        <FormControl
          variant={variant}
          error={props.error}
          fullWidth={fullWidth}
        >
          {inputLabel && (
            <InputLabel id={resolvedLabelId}>{inputLabel}</InputLabel>
          )}

          <Select
            {...rest}
            id={resolvedSelectId}
            labelId={inputLabel ? resolvedLabelId : undefined}
            aria-label={
              !inputLabel ? String(placeholder || "Sélection") : undefined
            }
            onChange={handleChange}
            fullWidth={fullWidth}
            displayEmpty
            open={disableOpen ? false : isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            SelectDisplayProps={{
              tabIndex: props.disabled ? -1 : 0,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (disableOpen || props.disabled) {
                  if ([" ", "Enter", "ArrowDown", "ArrowUp"].includes(e.key)) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  return;
                }

                if ([" ", "Enter", "ArrowDown", "ArrowUp"].includes(e.key)) {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(true);
                }
              },
            }}
            className={leftIconClassName || leftIcon ? "select-icon" : ""}
            value={currentValue ?? (props.multiple ? [] : "")}
            MenuProps={
              children
                ? {
                    sx: {
                      "& .MuiMenu-paper": {
                        minWidth: "0!important",
                      },
                    },
                  }
                : props.MenuProps
            }
            renderValue={handleRenderValue}
            IconComponent={() =>
              loading ? (
                <CircularProgress
                  size={20}
                  sx={{
                    position: "absolute",
                    right: "10px",
                    color: "primary.main",
                  }}
                />
              ) : (
                <EmtIcon
                  className={
                    iconClass ? iconClass : isOpen ? "emt-up-1" : "emt-down-1"
                  }
                  sx={{
                    position: "absolute",
                    right: "6px",
                    fontSize: iconClass ? "20px" : "initial",
                  }}
                  aria-hidden="true"
                />
              )
            }
          >
            {loading ? (
              <MenuItem disabled>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    py: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              </MenuItem>
            ) : children ? (
              <>{children}</>
            ) : (
              options.map((option, key) =>
                option.subHeader ? (
                  <StyledListSubheader key={String(option.value)}>
                    <EmtTypography variant="body2">
                      {option.label}
                    </EmtTypography>
                  </StyledListSubheader>
                ) : (
                  <StyledMenuItem
                    key={String(option.value)}
                    value={option.value}
                    disabled={option.disabled} // Add this line
                  >
                    <MenuItemBox
                      className={
                        isOptionSelected(currentValue, option, props.multiple)
                          ? "active"
                          : ""
                      }
                      sx={{
                        justifyContent: option.media
                          ? "flex-start"
                          : "space-between",
                        gap: option.media ? "15px" : 0,
                      }}
                      color={color}
                    >
                      {/*  @ts-ignore */}
                      {option.media && (
                        <Box sx={{ display: "flex" }}>
                          <>{option.media}</>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <EmtTypography
                          variant="body1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {option.label}
                        </EmtTypography>
                        {option.caption && (
                          <EmtTypography variant="caption">
                            {option.caption}
                          </EmtTypography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {isOptionSelected(
                          currentValue,
                          option,
                          props.multiple
                        ) && <EmtIcon className="emt-tick" />}
                      </Box>
                    </MenuItemBox>
                  </StyledMenuItem>
                )
              )
            )}
          </Select>
        </FormControl>
      </Box>
      {helperText && (
        <StyledFormHelperText
          color={props.color}
          className={props.error ? "error" : ""}
          sx={{
            "& *": {
              font: "Nunito Sans,Helvetica,Arial,sans-serif!important",
            },
          }}
        >
          {helperText}
        </StyledFormHelperText>
      )}
    </Box>
  );
}
